// src/components/inventory/TablaInventario.js
//
// Tabla de inventario reutilizable. Las tres vistas (ingredientes, productos,
// bebidas) eran el mismo código repetido; aquí vive una sola vez.
//
// Aporta tres cosas que antes no existían:
//   1. Detección de cambios sin guardar: los botones de guardar solo aparecen
//      en la fila que tocaste, y avisa antes de salir si queda algo pendiente.
//   2. Confirmación antes de eliminar, con el nombre del registro a la vista.
//   3. Mensajes de éxito y de error visibles, no solo en la consola.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import '../../styles/Inventario.css';

const numero = (n) => Number(n || 0).toLocaleString('es-CO');
const dinero = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

export const formatearCelda = { numero, dinero };

function TablaInventario({
  titulo,
  descripcion,
  recurso,            // '/api/ingredientes'
  idKey,              // 'ingredient_id'
  columnas,           // [{ clave, etiqueta, tipo, alinear, placeholder, enCreacion, paso }]
  nuevoVacio,
  aPayloadCrear = (n) => n,
  estadoFila,         // (fila) => { tono, texto } | null
  columnaExtra,       // { etiqueta, render(fila) }
  etiquetaBuscar = 'Buscar…',
  extraFormulario,    // (nuevo, setNuevo) => nodo, para campos no genéricos
  accionesFila,       // (fila) => nodo, acciones propias antes de las estándar
  registrarRecarga,   // (fn) => void, expone el recargador al componente padre
  isReadOnly,
}) {
  const apiUrl = process.env.REACT_APP_API_URL;

  const [filas, setFilas] = useState([]);
  const [originales, setOriginales] = useState({});
  const [filtro, setFiltro] = useState('');
  const [nuevo, setNuevo] = useState(nuevoVacio);
  const [porEliminar, setPorEliminar] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const refModal = useRef(null);

  const editables = useMemo(() => columnas.filter((c) => c.tipo !== 'lectura'), [columnas]);

  const instantanea = useCallback(
    (fila) => JSON.stringify(editables.map((c) => fila[c.clave])),
    [editables]
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await axios.get(`${apiUrl}${recurso}`);
      const lista = Array.isArray(data) ? data : [];
      setFilas(lista);
      const mapa = {};
      lista.forEach((f) => { mapa[f[idKey]] = instantanea(f); });
      setOriginales(mapa);
    } catch {
      toast.error(`No se pudo cargar ${titulo.toLowerCase()}`);
    } finally {
      setCargando(false);
    }
  }, [apiUrl, recurso, idKey, instantanea, titulo]);

  useEffect(() => { cargar(); }, [cargar]);

  // El padre necesita poder refrescar la tabla tras editar algo en su propio
  // diálogo (la receta de un producto, por ejemplo).
  useEffect(() => { if (registrarRecarga) registrarRecarga(cargar); }, [registrarRecarga, cargar]);

  const sucia = (fila) => originales[fila[idKey]] !== undefined
    && originales[fila[idKey]] !== instantanea(fila);

  const pendientes = filas.filter(sucia).length;

  // Avisar si el navegador se va a cerrar con cambios sin guardar.
  useEffect(() => {
    if (!pendientes) return;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [pendientes]);

  const cambiar = (id, clave, valor) =>
    setFilas((prev) => prev.map((f) => (f[idKey] === id ? { ...f, [clave]: valor } : f)));

  const guardar = async (fila) => {
    const id = fila[idKey];
    setGuardando(id);
    try {
      await axios.put(`${apiUrl}${recurso}/${id}`, fila);
      setOriginales((prev) => ({ ...prev, [id]: instantanea(fila) }));
      toast.success('Cambios guardados');
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setGuardando(null);
    }
  };

  const descartar = (id) => { cargar(); };

  const eliminar = async () => {
    const fila = porEliminar;
    setPorEliminar(null);
    try {
      await axios.delete(`${apiUrl}${recurso}/${fila[idKey]}`);
      toast.success('Registro eliminado');
      cargar();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const crear = async () => {
    const faltante = columnas.find(
      (c) => c.enCreacion !== false && c.requerido && !String(nuevo[c.clave] ?? '').trim()
    );
    if (faltante) return toast.error(`Falta ${faltante.etiqueta.toLowerCase()}`);
    try {
      await axios.post(`${apiUrl}${recurso}`, aPayloadCrear(nuevo));
      toast.success('Registro creado');
      setNuevo(nuevoVacio);
      setMostrarForm(false);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.message || 'No se pudo crear');
    }
  };

  // Escape cierra el diálogo de borrado.
  useEffect(() => {
    if (!porEliminar) return;
    refModal.current?.querySelector('button')?.focus();
    const alPulsar = (e) => { if (e.key === 'Escape') setPorEliminar(null); };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [porEliminar]);

  const filtradas = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return filas;
    return filas.filter((f) => String(f.name ?? '').toLowerCase().includes(q));
  }, [filas, filtro]);

  const camposCreacion = columnas.filter((c) => c.enCreacion !== false && c.tipo !== 'lectura');

  return (
    <div className="inventario-vista">
      <div className="encabezado-seccion">
        <div>
          <h2>{titulo}</h2>
          {descripcion && <p className="texto-auxiliar">{descripcion}</p>}
        </div>
        <div className="fila">
          {pendientes > 0 && (
            <span className="distintivo aviso">
              {pendientes} {pendientes === 1 ? 'cambio sin guardar' : 'cambios sin guardar'}
            </span>
          )}
          {!isReadOnly && (
            <button onClick={() => setMostrarForm((v) => !v)}>
              {mostrarForm ? 'Cerrar' : 'Agregar'}
            </button>
          )}
        </div>
      </div>

      {!isReadOnly && mostrarForm && (
        <div className="form-agregar">
          <div className="campos-agregar">
            {camposCreacion.map((c) => (
              <div className="campo" key={c.clave}>
                <label htmlFor={`nuevo-${c.clave}`}>{c.etiqueta}</label>
                <input
                  id={`nuevo-${c.clave}`}
                  type={c.tipo === 'numero' || c.tipo === 'dinero' ? 'number' : 'text'}
                  step={c.paso || (c.tipo === 'dinero' ? '1' : 'any')}
                  min={c.tipo === 'text' ? undefined : 0}
                  placeholder={c.placeholder || ''}
                  value={nuevo[c.clave] ?? ''}
                  onChange={(e) => setNuevo({ ...nuevo, [c.clave]: e.target.value })}
                />
              </div>
            ))}
          </div>
          {extraFormulario && extraFormulario(nuevo, setNuevo)}
          <div className="fila-fin">
            <button className="cancelar" onClick={() => { setNuevo(nuevoVacio); setMostrarForm(false); }}>
              Cancelar
            </button>
            <button onClick={crear}>Guardar</button>
          </div>
        </div>
      )}

      <div className="campo buscador-inv">
        <label htmlFor={`buscar-${idKey}`} className="solo-lectores">Buscar</label>
        <input id={`buscar-${idKey}`} type="search" value={filtro}
          placeholder={etiquetaBuscar} onChange={(e) => setFiltro(e.target.value)} />
      </div>

      {filtradas.length === 0 ? (
        <div className="estado-vacio">
          <h3>{cargando ? 'Cargando…' : 'Sin registros'}</h3>
          <p>{cargando ? '' : filtro ? 'Prueba con otro término.' : 'Agrega el primero con el botón de arriba.'}</p>
        </div>
      ) : (
        <div className="tabla-scroll">
          <table>
            <thead>
              <tr>
                {columnas.map((c) => (
                  <th key={c.clave} className={c.alinear === 'derecha' ? 'cifra' : ''}>{c.etiqueta}</th>
                ))}
                {estadoFila && <th>Estado</th>}
                {columnaExtra && <th>{columnaExtra.etiqueta}</th>}
                {!isReadOnly && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtradas.map((fila) => {
                const id = fila[idKey];
                const cambiada = sucia(fila);
                const estado = estadoFila ? estadoFila(fila) : null;
                return (
                  <tr key={id} className={cambiada ? 'fila-sucia' : ''}>
                    {columnas.map((c) => (
                      <td key={c.clave} className={c.alinear === 'derecha' ? 'cifra' : ''}>
                        {isReadOnly || c.tipo === 'lectura' ? (
                          c.tipo === 'dinero' ? dinero(fila[c.clave])
                            : c.tipo === 'numero' ? numero(fila[c.clave])
                            : fila[c.clave]
                        ) : (
                          <input
                            className="celda-editable"
                            type={c.tipo === 'numero' || c.tipo === 'dinero' ? 'number' : 'text'}
                            step={c.paso || (c.tipo === 'dinero' ? '1' : 'any')}
                            value={fila[c.clave] ?? ''}
                            aria-label={`${c.etiqueta} de ${fila.name}`}
                            onChange={(e) => cambiar(id, c.clave, e.target.value)}
                          />
                        )}
                      </td>
                    ))}
                    {estadoFila && (
                      <td>{estado && <span className={`distintivo ${estado.tono}`}>{estado.texto}</span>}</td>
                    )}
                    {columnaExtra && <td>{columnaExtra.render(fila)}</td>}
                    {!isReadOnly && (
                      <td>
                        <div className="acciones-fila">
                          {accionesFila && accionesFila(fila)}
                          {cambiada ? (
                            <>
                              <button className="pequeno" onClick={() => guardar(fila)}
                                disabled={guardando === id} data-ocupado={guardando === id}>
                                {guardando === id ? 'Guardando…' : 'Guardar'}
                              </button>
                              <button className="fantasma pequeno" onClick={() => descartar(id)}>
                                Descartar
                              </button>
                            </>
                          ) : (
                            <button className="borrar-button pequeno" onClick={() => setPorEliminar(fila)}>
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {porEliminar && (
        <div className="modal-inv-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setPorEliminar(null); }}>
          <div className="modal-inv" ref={refModal} role="dialog" aria-modal="true"
            aria-label="Confirmar eliminación">
            <h3>¿Eliminar “{porEliminar.name}”?</h3>
            <p className="texto-auxiliar">
              Esta acción no se puede deshacer. El registro desaparece del inventario.
            </p>
            <div className="fila-fin">
              <button className="cancelar" onClick={() => setPorEliminar(null)}>Cancelar</button>
              <button className="borrar-button" onClick={eliminar}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TablaInventario;
